import ignore from 'ignore';
import { selectionStore, type Framework, type Language } from '~/lib/persistence/selectionStore';
import type { ActionCallbackData } from '~/lib/runtime/message-parser';
import { workbenchStore } from '~/lib/stores/workbench';
import * as Sentry from '@sentry/remix';
import { userStore } from '~/lib/stores/user';

// Map frameworks to their GitHub repositories
const templateRepos = {
  react_tailwind: {
    org: 'dualiteindia',
    repo: 'react-tailwind-template',
  },
  react_chakra: {
    org: 'dualiteindia',
    repo: 'react-chakra-template',
  },
  react_shadcn: {
    org: 'dualiteindia',
    repo: 'react-shadcn-template',
  },
  html_css: {
    org: 'dualiteindia',
    repo: 'html-css-template',
  },
  html_tailwind: {
    org: 'dualiteindia',
    repo: 'html-tailwind-template',
  },
  angular_css: {
    org: 'dualiteindia',
    repo: 'angular-css-template',
  },
  vue_tailwind: {
    org: 'dualiteindia',
    repo: 'vue-tailwind-template',
  },
  reactnative: {
    org: 'dualiteindia',
    repo: 'react-native-template',
  },
};

async function fetchFromGitHub(framework: string, language: Language) {
  console.log(`[getTemplates] Starting fetchFromGitHub for framework=${framework}, language=${language}`);

  // Get repo info from map
  const repoInfo = templateRepos[framework as keyof typeof templateRepos];

  if (!repoInfo) {
    console.error(`[getTemplates] No repository configured for ${framework}`);
    throw new Error(`No repository configured for ${framework}`);
  }

  const { org, repo } = repoInfo;
  const path = language === 'ts' ? 'ts/template' : 'js/template';
  console.log(`[getTemplates] Fetching from GitHub: org=${org}, repo=${repo}, path=${path}`);

  try {
    // Fetch directory listing from GitHub
    const fetchUrl = `https://api.github.com/repos/${org}/${repo}/contents/${path}`;
    console.log(`[getTemplates] Fetching directory from: ${fetchUrl}`);

    const startTime = Date.now();
    const listResponse = await fetch(fetchUrl);
    console.log(
      `[getTemplates] GitHub API response received in ${Date.now() - startTime}ms, status=${listResponse.status}`,
    );

    if (!listResponse.ok) {
      console.error(`[getTemplates] GitHub API error: ${listResponse.status} ${listResponse.statusText}`);
      throw new Error(`GitHub API error: ${listResponse.statusText}`);
    }

    const items = await listResponse.json();
    console.log(`[getTemplates] GitHub API returned ${Array.isArray(items) ? items.length : 'non-array'} items`);

    // Recursively fetch all files
    if (!Array.isArray(items)) {
      console.error(`[getTemplates] Expected array but received:`, items);
      throw new Error('GitHub API returned unexpected format');
    }

    const files = await fetchDirectoryContents(org, repo, items, path);
    console.log(`[getTemplates] Successfully fetched ${files.length} files from GitHub`);
    return files;
  } catch (error) {
    console.error(`[getTemplates] Error fetching from GitHub:`, error);
    throw error;
  }
}

async function fetchDirectoryContents(org: string, repo: string, items: any[], basePath: string): Promise<any[]> {
  console.log(`[getTemplates] Fetching directory contents: basePath=${basePath}, items=${items.length}`);

  const files: any[] = [];

  for (const item of items) {
    if (item.type === 'file') {
      console.log(`[getTemplates] Processing file: ${item.path}`);

      const contentResponse = await fetch(item.download_url);

      if (!contentResponse.ok) {
        console.warn(
          `[getTemplates] Failed to fetch file ${item.path}: ${contentResponse.status} ${contentResponse.statusText}`,
        );
        continue;
      }

      const content = await contentResponse.text();
      const filePath = item.path.replace(`${basePath}/`, '');
      console.log(`[getTemplates] File processed: ${filePath} (${content.length} bytes)`);

      files.push({
        name: item.name,
        path: filePath,
        content,
      });
    } else if (item.type === 'dir') {
      console.log(`[getTemplates] Processing directory: ${item.path}`);

      const dirUrl = `https://api.github.com/repos/${org}/${repo}/contents/${item.path}`;
      console.log(`[getTemplates] Fetching directory contents from: ${dirUrl}`);

      const dirResponse = await fetch(dirUrl);

      if (!dirResponse.ok) {
        console.warn(
          `[getTemplates] Failed to fetch directory ${item.path}: ${dirResponse.status} ${dirResponse.statusText}`,
        );
        continue;
      }

      const dirItems = await dirResponse.json();
      console.log(
        `[getTemplates] Directory ${item.path} contains ${Array.isArray(dirItems) ? dirItems.length : 'non-array'} items`,
      );

      if (!Array.isArray(dirItems)) {
        console.warn(`[getTemplates] Expected array for directory items but received:`, dirItems);
        continue;
      }

      const nestedFiles = await fetchDirectoryContents(org, repo, dirItems, basePath);
      console.log(`[getTemplates] Fetched ${nestedFiles.length} nested files from ${item.path}`);
      files.push(...nestedFiles);
    }
  }

  return files;
}

export async function getTemplates(framework: Framework, language: Language, title?: string) {
  console.log(`[getTemplates] Starting getTemplates for framework=${framework}, language=${language} title=${title}`);

  // Instead of fetching from our API, fetch directly from GitHub
  let files;

  const startTime = Date.now();
  console.log(`[getTemplates] Beginning GitHub fetch at ${new Date().toISOString()}`);

  try {
    files = await fetchFromGitHub(framework, language);
    console.log(`[getTemplates] GitHub fetch completed in ${Date.now() - startTime}ms, received ${files.length} files`);
  } catch (error) {
    Sentry.captureException(error, {
      extra: { framework, language },
    });
    console.error(`[getTemplates] Failed to fetch template from GitHub:`, error);
    throw new Error('Failed to fetch template');
  }

  console.log(`[getTemplates] Starting template processing for ${files.length} files`);

  let filteredFiles = files;

  // exclude .dualite
  const beforeDualiteFilter = filteredFiles.length;
  filteredFiles = filteredFiles.filter((x: any) => x.path.startsWith('.dualite') === false);
  console.log(`[getTemplates] Excluded ${beforeDualiteFilter - filteredFiles.length} .dualite files`);

  // check for ignore file in .dualite folder
  const templateIgnoreFile = files.find((x: any) => x.path.startsWith('.dualite') && x.name === 'ignore');
  console.log(`[getTemplates] Found ignore file: ${templateIgnoreFile ? 'yes' : 'no'}`);

  const filesToImport = {
    files: filteredFiles,
    ignoreFile: [] as typeof filteredFiles,
  };

  if (templateIgnoreFile) {
    // redacting files specified in ignore file
    const ignorepatterns = templateIgnoreFile.content.split('\n').map((x: string) => x.trim());
    console.log(`[getTemplates] Ignore patterns:`, ignorepatterns);

    const ig = ignore().add(ignorepatterns);

    const ignoredFiles = filteredFiles.filter((x: any) => ig.ignores(x.path));
    console.log(`[getTemplates] Found ${ignoredFiles.length} files to ignore`);

    filteredFiles = filteredFiles.filter((x: any) => !ig.ignores(x.path));
    console.log(`[getTemplates] After ignore filtering: ${filteredFiles.length} files remain`);

    filesToImport.files = filteredFiles;
    filesToImport.ignoreFile = ignoredFiles;
  }

  // Add the files to workbench store which are not present in prompt
  console.log(`[getTemplates] Adding ${filesToImport.ignoreFile.length} ignored files to workbench store`);
  workbenchStore.addArtifact({
    id: undefined,
    title: undefined,
    messageId: 'import-files-not-in-prompt',
  });

  filesToImport.ignoreFile.forEach((file: any, index: number) => {
    console.log(`[getTemplates] Adding ignored file to workbench: ${file.path}`);

    const data: ActionCallbackData = {
      artifactId: 'import-ignored-files',
      messageId: `import-files-not-in-prompt`,
      actionId: `action-${Date.now()}-${index}`,
      action: {
        type: 'file',
        filePath: file.path,
        content: file.content,
      },
    };
    workbenchStore.addAction(data);
    workbenchStore.runAction(data);
  });

  console.log(`[getTemplates] Generating assistant message with ${filesToImport.files.length} files`);

  const assistantMessage = `
  <dualiteArtifact id="imported-files" title="${title || 'Importing Starter Files'}" type="bundled">
  ${filesToImport.files
    .map(
      (file: any) =>
        `<dualiteAction type="file" filePath="${file.path}">
  ${file.content}
  </dualiteAction>`,
    )
    .join('\n')}
  </dualiteArtifact> 
  `;

  // Don't log the entire assistantMessage as it could be very large
  console.log(`[getTemplates] Assistant message generated (${assistantMessage.length} chars)`);

  let userMessage = ``;
  const templatePromptFile = files
    .filter((x: any) => x.path.startsWith('.dualite'))
    .find((x: any) => x.name === 'prompt');
  console.log(`[getTemplates] Found prompt file: ${templatePromptFile ? 'yes' : 'no'}`);

  if (templatePromptFile) {
    console.log(`[getTemplates] Adding template instructions from prompt file`);
    userMessage = `
  TEMPLATE INSTRUCTIONS:
  ${templatePromptFile.content}

  IMPORTANT: Don't Forget to install the dependencies before running the app
  IMPORTANT: Don't Forget to run the \`npm run dev\` command to start the development server after installing the dependencies.
  ---
  `;
  }

  if (filesToImport.ignoreFile.length > 0) {
    console.log(`[getTemplates] Adding ${filesToImport.ignoreFile.length} ignored files information to user message`);
    userMessage =
      userMessage +
      `
  FOLLOWING COMPONENTS ARE PRESENT IN THE SYSTEM BUT NOT SHOWN TO YOU BUT YOU CAN IMPORT THEM AND USE THEM IN YOUR APP:
  ${filesToImport.ignoreFile.map((file: any) => `- ${file.path}`).join('\n')}

  Permitted actions:
  ✓ Import these files as COMPONENTS

  If you need to make changes to functionality, create new files instead of modifying the protected ones listed above.
  ---
  `;
  }

  userMessage += `
  ---
  Template files have been imported successfully. Here are your next steps:

  DEVELOPMENT SETUP:
  1. Install project dependencies:
     \`npm install\`
  2. Start development server:
     \`npm run dev\`

  ${
    framework === 'react_shadcn'
      ? `COMPONENT SETUP:
  To use Shadcn UI components, install them first:
  \`echo "yes" | npx shadcn@latest add <component-1-name> <component-2-name>\`
  `
      : ''
  }

  DEVELOPMENT GUIDELINES:
  • You can modify any existing files
  • Create new files as needed
  • Use any imported components and utilities
  • Follow the project's coding standards

  Please proceed with the implementation based on the requirements provided.
  ---
  `;

  console.log(`[getTemplates] User message generated (${userMessage.length} chars)`);
  console.log(`[getTemplates] Template processing completed in ${Date.now() - startTime}ms`);

  return {
    assistantMessage,
    userMessage,
  };
}

const extractRepoInfo = (url: string) => {
  try {
    const parsedUrl = new URL(url);
    const parts = parsedUrl.pathname.replace(/^\/|\/$/g, '').split('/');

    if (parts.length < 2) {
      return null;
    }

    const owner = parts[0];
    const repo = parts[1];

    if (!owner || !repo) {
      return null;
    }

    return { owner, repo };
  } catch (err) {
    Sentry.captureException(err, {
      extra: { url },
    });
    console.error('>>>[extractRepoInfo] error: ', err);
    return null;
  }
};

interface GitHubRepo {
  name: string;
  private: boolean;
  visibility: string;
}

const fetchRepositoryContent = async (
  repoUrl: string,
  repoInfo: { owner: string; repo: string },
): Promise<Array<{ path: string; content: string; name: string }> | undefined> => {
  const repoFiles: Array<{ path: string; download_url: string }> = [];
  const repoFilesWContent: Array<{ path: string; content: string; name: string }> = [];

  // Add helper function to check for asset files
  const isAssetFile = (path: string): boolean => {
    const assetExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.ico', '.webp', '.bmp', '.tiff'];
    return assetExtensions.some((ext) => path.toLowerCase().endsWith(ext));
  };

  if (!repoInfo) {
    console.log('Invalid repository URL. Please enter a URL in the format: https://github.com/owner/repository');
    return undefined;
  }

  // Recursive fetcher
  const fetchDirectory = async (path = '') => {
    const url = `https://api.github.com/repos/${repoInfo.owner}/${repoInfo.repo}/contents/${path}`;
    const accessToken = selectionStore.get().githubAccessToken;

    try {
      const res = await Sentry.startSpan(
        {
          name: 'getTemplates.fetchDirectory',
          op: 'getTemplates.fetchDirectory',
        },
        async () => {
          const response = await fetch(url, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              Accept: 'application/vnd.github.v3+json',
            },
          });

          if (!response.ok) {
            const errorData = (await response.json()) as { message: string };
            throw new Error(errorData.message || 'Failed to fetch repository content');
          }

          const data: Array<{ type: string; path: string; download_url: string }> = await response.json();

          for (const item of data) {
            if (item.type === 'file') {
              // Skip asset files
              if (isAssetFile(item.path)) {
                console.log(`Skipping asset file: ${item.path}`);
                continue;
              }

              repoFiles.push({
                path: item.path,
                download_url: item.download_url,
              });
            } else if (item.type === 'dir') {
              await fetchDirectory(item.path);
            }
          }
        },
      );
      return res;
    } catch (err) {
      console.error(`Error fetching path "${path}":`, err);
      throw err; // Propagate error to main try-catch
    }
  };

  try {
    await fetchDirectory();

    console.log('>>>[fetchRepositoryContent] repoFiles: ', repoFiles);
    console.log('✅ Full repository file list:', repoFiles);

    // Add the files to workbench store
    const artifactId = `${repoInfo.repo}`;
    workbenchStore.addArtifact({
      id: artifactId,
      title: repoInfo.repo || 'GitHub Import',
      messageId: 'github-files-import',
    });

    // Process each file and add it to the workbench
    for (const file of repoFiles) {
      // Skip unnecessary files

      const fileName = file.path.split('/').pop();

      if (
        file.path.includes('node_modules') ||
        (fileName && fileName.startsWith('.')) ||
        fileName === 'package-lock.json' ||
        fileName === 'yarn.lock' ||
        fileName === 'pnpm-lock.yaml'
      ) {
        console.log(`Skipping hidden or unnecessary file: ${file.path}`);
        continue;
      }

      try {
        await Sentry.startSpan(
          {
            name: 'getTemplates.fetchRepositoryContent',
            op: 'getTemplates.fetchRepositoryContent',
          },
          async () => {
            // Fetch the actual content of each file
            const response = await fetch(file.download_url);
            const content = await response.text();
            console.log('>>>[fetchRepositoryContent] content: ', response, 'content: ', content);

            repoFilesWContent.push({
              path: file.path,
              content,
              name: file.path,
            });

            const actionData: ActionCallbackData = {
              artifactId,
              messageId: 'github-files-import',
              actionId: `github-import-${Date.now()}-${file.path}`,
              action: {
                type: 'file',
                filePath: file.path,
                content,
              },
            };

            workbenchStore.addAction(actionData);
            workbenchStore.runAction(actionData);
          },
        );
      } catch (err) {
        console.error(`Failed to fetch content for ${file.path}:`, err);
        throw err;
      }
    }

    return repoFilesWContent;
  } catch (err) {
    Sentry.captureException(err, {
      extra: { repoUrl, repoInfo },
    });
    console.log(err instanceof Error ? err.message : 'An unexpected error occurred while fetching the repository.');
    console.error(err);
    return undefined;
  }
};

// Add return type interface
interface TemplateResult {
  assistantMessage: string;
  userMessage: string;
}

// Add return type annotation to function
export async function getGithubCode(
  framework: Framework,
  language: Language,
  githubRepo: string,
): Promise<TemplateResult> {
  console.log('>>>[getGithubCode] githubRepo: ', githubRepo);

  if (!githubRepo) {
    console.log('Invalid repository URL. Please enter a URL in the format: https://github.com/owner/repository');
    return { assistantMessage: '', userMessage: '' };
  }

  const startTime = Date.now();
  const repoInfo = extractRepoInfo(githubRepo);
  console.log('>>>[getGithubCode] repoInfo: ', repoInfo);

  if (!repoInfo) {
    console.log('Invalid repository URL. Please enter a URL in the format: https://github.com/owner/repository');
    return { assistantMessage: '', userMessage: '' };
  }

  const files = await fetchRepositoryContent(githubRepo, repoInfo);
  console.log('>>>[getTemplates] files: ', files);

  if (!files) {
    return { assistantMessage: '', userMessage: '' };
  }

  const assistantMessage = `
  <dualiteArtifact id="imported-files" title="${repoInfo.repo || 'Importing All Existing Files'}" type="bundled">
  ${files
    .map(
      (file: any) =>
        `<dualiteAction type="file" filePath="${file.path}">
  ${file.content}
  </dualiteAction>`,
    )
    .join('\n')}
  </dualiteArtifact>
  `;

  // Don't log the entire assistantMessage as it could be very large
  console.log(`[getTemplates] Assistant message generated (${assistantMessage.length} chars)`);

  let userMessage = ``;
  const templatePromptFile = files
    .filter((x: any) => x.path.startsWith('.dualite'))
    .find((x: any) => x.name === 'prompt');
  console.log(`[getTemplates] Found prompt file: ${templatePromptFile ? 'yes' : 'no'}`);

  if (templatePromptFile) {
    console.log(`[getTemplates] Adding template instructions from prompt file`);
    userMessage = `
  CODE INSTRUCTIONS:
  ${templatePromptFile.content}
  
  IMPORTANT: Don't Forget to install the dependencies before running the app
  IMPORTANT: Don't Forget Analyse the content of package.json file and run the \`npm run dev\` | \`npm run start\` command accordingly to start the development server after installing the dependencies.
  ---
  `;
  }

  userMessage += `
  ---
  The code files have been imported successfully. Here are your next steps:

  DEVELOPMENT SETUP:
  1. Install project dependencies:
     \`npm install\`
  2. Start development server:
     \`npm run dev\` | \`npm run start\`

  ${
    framework === 'react_shadcn'
      ? `COMPONENT SETUP:
  To use Shadcn UI components, install them first:
  \`echo "yes" | npx shadcn@latest add <component-1-name> <component-2-name>\`
  `
      : ''
  }

  DEVELOPMENT GUIDELINES:
  • You can modify any existing files
  • Use any imported components and utilities
  • Follow the project's coding standards

  Please proceed with the implementation based on the requirements provided.
  ---
  `;

  console.log(`[getTemplates] User message generated (${userMessage.length} chars)`);
  console.log(`[getTemplates] Template processing completed in ${Date.now() - startTime}ms`);

  return {
    assistantMessage,
    userMessage,
  };
}
